'use client';
import React from 'react';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import ProductHeading from '@components/Product/Details/_ProductHeading';
import { useCurrencyString } from '@repo/ui/src/hooks/useCurrencyString';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { LinkProps } from '@repo/ui/src/types/link';
import { classNames } from '@repo/ui/src/utils/classNames';
import Product from '@models/product';
import { useProductContext } from '@context/ProductProvider';
import CallToActionButton from '@components/Product/CallToActionButton';
import { PDPTemplate } from '@views/ProductDetailPage/_enums';
import AlternateSizes from '../AlternateGrillSizes';
import AlternateColors, { COLOR_STYLES } from './_AlternateColors';
import AdditionalInformation from './_AdditionalInformation';

export interface ProductColor {
  color: keyof typeof COLOR_STYLES;
  link: LinkProps;
  sku: string;
}

export interface AlternateColorProps {
  colors: Array<ProductColor>;
}
interface Props {
  template: PDPTemplate;
  dark?: boolean;
  promoMarkdown?: string;
  alternateSize?: Product;
  alternateColors?: {
    colors: Array<ProductColor>;
  };
  ownerManual?: {
    ownerManualLink: Array<LinkProps>;
  };
  prop65?: {
    title: string;
    warningBody: string;
  };
}

const ProductSelection = ({
  template,
  ownerManual,
  prop65,
  dark,
  promoMarkdown,
  alternateSize,
  alternateColors,
}: Props) => {
  const { eCommEnabled } = useCurrentUrl();

  const { t } = useTranslation();
  const getCurrencyString = useCurrencyString();
  const {
    totalPrice: _totalPrice,
    addToCart,
    isLoadingAddToCart,
    mainProduct: product,
  } = useProductContext();

  const [quantity] = React.useState(1);
  const totalPrice = React.useMemo(() => {
    return _totalPrice && _totalPrice !== '0'
      ? Number(_totalPrice)
      : [product].reduce((val, item) => val + (item?.price ?? 0), 0);
  }, [_totalPrice, product]);

  const colorsStep = alternateSize ? 2 : 1;

  const showPrice =
    ![PDPTemplate.Grill, PDPTemplate.PDPV2].includes(template) &&
    product?.purchasing &&
    !product.purchasing.hideCTA;

  if (!product) {
    return null;
  }

  return (
    <div>
      <ProductHeading showPrice={showPrice} dark={dark} className="md:block hidden" />
      {alternateSize && (
        <AlternateSizes alternateSize={alternateSize} product={product} dark={dark} />
      )}
      {(alternateColors?.colors?.length ?? 0) > 0 && (
        <AlternateColors
          color={product.color}
          colors={alternateColors!.colors}
          dark={dark}
          step={colorsStep}
        />
      )}
      <div className="w-full">
        {eCommEnabled && (
          <>
            {template === PDPTemplate.Grill && (
              <p className="flex between pt-2">
                <span className="tra-text-web-heading-6">
                  {t('productDetailPageContent.estimatedTotalText', undefined, {
                    default: 'Estimated Total',
                  })}
                </span>
                <span className="tra-text-web-heading-3">{getCurrencyString(totalPrice)}</span>
              </p>
            )}
          </>
        )}
        <div className={classNames('flex items-stretch', showPrice && 'mb-8')}>
          <CallToActionButton
            product={product}
            buttonProcessing={isLoadingAddToCart}
            processingText=""
            customProductPrice={totalPrice}
            buttonProps={{
              disabled: isLoadingAddToCart,
              full: true,
              tabIndex: 0,
              className: isLoadingAddToCart ? 'disabled:bg-brand-300' : 'disabled:bg-warm-400',
            }}
            onClickAddToCart={() => addToCart(quantity)}
          />
        </div>
        {eCommEnabled && promoMarkdown && (
          <div
            className="dangerously-set-markdown-body break-words"
            dangerouslySetInnerHTML={{ __html: promoMarkdown }}
          />
        )}
      </div>
      <AdditionalInformation
        dark={dark}
        ownerManual={ownerManual}
        prop65={prop65}
        showGrillAssistanceInfo={[
          PDPTemplate.Grill,
          PDPTemplate.PDPV2,
          PDPTemplate.PDPV2Light,
        ].includes(template)}
      />
    </div>
  );
};

export default ProductSelection;
